/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Helper function to format date components to MM-DD-YYYY or YYYY-MM-DD
 * @param {Date} dateObj - The Date object to format
 * @param {string} pattern - The pattern ('MM-DD-YYYY' or 'YYYY-MM-DD')
 * @returns {string} - The formatted date string
 */
const formatDateComponents = (dateObj, pattern) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  switch (pattern) {
    case 'MM-DD-YYYY':
      return `${month}-${day}-${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    default:
      return '';
  }
};

/**
 * Helper function to parse YYYY-MM-DD format with timezone safety and validation
 * @param {string} dateString - The YYYY-MM-DD date string
 * @returns {Date|null} - The parsed Date object or null if invalid
 */
const parseYYYYMMDD = dateString => {
  const [year, month, day] = dateString.split('-').map(Number);

  // Validate the date components before creating the Date object
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const dateObj = new Date(year, month - 1, day); // month is 0-indexed

  // Check if the date was auto-corrected by JavaScript
  if (
    dateObj.getFullYear() !== year ||
    dateObj.getMonth() !== month - 1 ||
    dateObj.getDate() !== day
  ) {
    return null;
  }

  return dateObj;
};

/**
 * Formats a date to MM-DD-YYYY format
 * @param {string|Date} date - The date to format
 * @returns {string} - The formatted date string in MM-DD-YYYY format
 */
export const formatDateToMMDDYYYY = date => {
  if (!date) return '';

  let dateObj;

  try {
    // Handle different input types
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      // Handle YYYY-MM-DD format specifically to avoid timezone issues
      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(date)) {
        dateObj = parseYYYYMMDD(date);
        if (!dateObj) {
          console.warn('formatDateToMMDDYYYY: Unable to parse date:', date);
          return '';
        }
      } else if (date.includes('/')) {
        // Handle MM/DD/YYYY or DD/MM/YYYY formats
        dateObj = new Date(date);
      } else if (date.includes('-')) {
        // Handle other dash formats
        dateObj = new Date(date);
      } else {
        // Try to parse as-is
        dateObj = new Date(date);
      }
    } else {
      console.warn('formatDateToMMDDYYYY: Unable to parse date:', date);
      return '';
    }

    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      console.warn('formatDateToMMDDYYYY: Invalid date:', date);
      return '';
    }

    return formatDateComponents(dateObj, 'MM-DD-YYYY');
  } catch (error) {
    console.error(
      'formatDateToMMDDYYYY: Error formatting date:',
      error,
      'Input:',
      date
    );
    return '';
  }
};

/**
 * Formats a date to YYYY-MM-DD format (ISO date string)
 * @param {string|Date} date - The date to format
 * @returns {string} - The formatted date string in YYYY-MM-DD format
 */
export const formatDateToYYYYMMDD = date => {
  if (!date) return '';

  let dateObj;

  try {
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else {
      console.warn('formatDateToYYYYMMDD: Unable to parse date:', date);
      return '';
    }

    if (isNaN(dateObj.getTime())) {
      console.warn('formatDateToYYYYMMDD: Invalid date:', date);
      return '';
    }

    return formatDateComponents(dateObj, 'YYYY-MM-DD');
  } catch (error) {
    console.error(
      'formatDateToYYYYMMDD: Error formatting date:',
      error,
      'Input:',
      date
    );
    return '';
  }
};

/**
 * Formats a date for display purposes (e.g., "January 15, 2024")
 * @param {string|Date} date - The date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - The formatted date string
 */
export const formatDateForDisplay = (date, options = {}) => {
  if (!date) return '';

  try {
    let dateObj;

    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      // Handle YYYY-MM-DD format specifically to avoid timezone issues
      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else {
        dateObj = new Date(date);
      }
    } else {
      return '';
    }

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    return dateObj.toLocaleDateString('en-US', {
      ...defaultOptions,
      ...options,
    });
  } catch (error) {
    console.error('formatDateForDisplay: Error formatting date:', error);
    return '';
  }
};

/**
 * Validates if a date string or Date object is valid
 * @param {string|Date} date - The date to validate
 * @returns {boolean} - True if the date is valid, false otherwise
 */
export const isValidDate = date => {
  if (!date) return false;

  try {
    let dateObj;

    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else {
      return false;
    }

    return !isNaN(dateObj.getTime());
  } catch (error) {
    return false;
  }
};

/**
 * Gets the current date in MM-DD-YYYY format
 * @returns {string} - Current date in MM-DD-YYYY format
 */
export const getCurrentDateMMDDYYYY = () => {
  return formatDateToMMDDYYYY(new Date());
};

/**
 * Gets the current date in YYYY-MM-DD format
 * @returns {string} - Current date in YYYY-MM-DD format
 */
export const getCurrentDateYYYYMMDD = () => {
  return formatDateToYYYYMMDD(new Date());
};
